import { APIResponseType } from 'src/Library/Types';
import { AdditionalChargeMasterOption } from 'src/components/pages/shipment-execution/SECommon/hooks/AdditionalChargesComponents/AdditionalCharges.types';
import {
  AdditionalChargeBulkUpdateResponse,
  AdditionalChargeColumnShape,
  AdditionalChargeLinePatch,
  AdditionalChargeLinesPayload,
  AdditionalChargeSubmitResponse,
  AdditionalChargeUploadUrlResponse,
} from 'src/components/pages/shipment-execution/SECommon/hooks/AdditionalChargesComponents/AdditionalChargeGrids.types';
import { API_BASE_URL } from '../globals';
import { GET, PATCH, POST, PUT } from './apiHandler';
import {
  ADDITIONAL_CHARGES_COLUMN_CONFIG,
  ADDITIONAL_CHARGES_DECLARE,
  ADDITIONAL_CHARGES_LINE_UPLOAD_URL,
  ADDITIONAL_CHARGES_LINES,
  ADDITIONAL_CHARGES_LINES_BULK_UPDATE,
  ADDITIONAL_CHARGES_MASTER,
  ADDITIONAL_CHARGES_SUBMIT,
} from './routes';

/**
 * Additional Vendor Charges catalog for the task's vendor type — the Part-1 declaration dropdown.
 *
 * Task-scoped: the catalog is the shipment owner's (shipper's) config, and the backend resolves the
 * owner org and the vendor type from the task itself. Passing `taskId` (not `vendorType`) is what lets
 * an assigned vendor — a different org — read the shipper's catalog instead of their own empty one.
 *
 * The backend already orders by `sequenceNumber`, so callers must render in the order received rather
 * than re-sorting; a client-side sort would mask an ordering regression on the server.
 */
export const getAdditionalChargeMaster = ({
  taskId,
}: {
  taskId: string;
}): Promise<APIResponseType<AdditionalChargeMasterOption[]>> => {
  return GET(`${API_BASE_URL}${ADDITIONAL_CHARGES_MASTER(taskId)}`);
};

/**
 * Additional Vendor Charges — Part 2 tabbed grids (ISS-291749 / C2).
 *
 * All shapes here are taken verbatim from the frozen contract:
 * `docs/superpowers/specs/2026-08-11-ISS-291742-291749-api-contract.md`.
 */

/**
 * The Part 1 -> Part 2 handoff. Fans out `additional_charge_line` rows for the declared `chargeCodes`
 * (one per BL or container per the charge's `applicabilityLevel`) and returns the full grouped lines
 * payload (D9) — the caller hands this straight to the grids rather than following up with a second
 * `GET /lines`. Idempotent against the partial-unique index (contract §3.1): re-declaring an
 * already-declared charge does not duplicate its rows, so calling this again on re-entry is safe.
 */
export const declareAdditionalCharges = ({
  taskId,
  chargeCodes,
}: {
  taskId: string;
  chargeCodes: string[];
}): Promise<APIResponseType<AdditionalChargeLinesPayload>> => {
  return POST(`${API_BASE_URL}${ADDITIONAL_CHARGES_DECLARE(taskId)}`, { chargeCodes });
};

/**
 * The 6 `(chargeType, applicabilityLevel)` column shapes. Static and org-independent — this **is** the
 * rendering contract; callers must build columns from it rather than hardcoding a per-tab column list.
 */
export const getAdditionalChargeColumnConfig = (): Promise<
  APIResponseType<AdditionalChargeColumnShape[]>
> => {
  return GET(`${API_BASE_URL}${ADDITIONAL_CHARGES_COLUMN_CONFIG}`);
};

/**
 * The grouped lines payload a task's grids render from. Line-sync runs server-side on every call — new
 * containers added after declaration get rows for every already-declared CONTAINER-level charge.
 */
export const getAdditionalChargeLines = ({
  taskId,
}: {
  taskId: string;
}): Promise<APIResponseType<AdditionalChargeLinesPayload>> => {
  return GET(`${API_BASE_URL}${ADDITIONAL_CHARGES_LINES(taskId)}`);
};

/**
 * Save as Draft. Mutable only while a line is `DRAFT` or `REJECTED` (enforced server-side). Absent key
 * on a line = untouched; explicit `null` clears it. One transaction, bulk update.
 */
export const updateAdditionalChargeLines = ({
  lines,
}: {
  lines: AdditionalChargeLinePatch[];
}): Promise<APIResponseType<AdditionalChargeBulkUpdateResponse>> => {
  return PATCH(`${API_BASE_URL}${ADDITIONAL_CHARGES_LINES_BULK_UPDATE}`, { lines });
};

/**
 * Presign step of the attachment write path (D4). Writes no DB rows — the caller must still `PUT` the
 * file to `post`, then send `{ url: get, fileName }` on the line in the next bulk PATCH.
 */
export const getAdditionalChargeLineUploadUrl = ({
  lineId,
  extension,
}: {
  lineId: string;
  extension: string;
}): Promise<APIResponseType<AdditionalChargeUploadUrlResponse>> => {
  return POST(`${API_BASE_URL}${ADDITIONAL_CHARGES_LINE_UPLOAD_URL(lineId)}`, { extension });
};

/**
 * `PUT`s the raw file to the presigned `post` URL from `getAdditionalChargeLineUploadUrl`.
 *
 * `file: any` mirrors `PUT`'s own signature in `src/api/apiHandler.ts` (and `uploadToS3` in
 * `src/api/invoice.ts`) verbatim — that shared helper is untyped on this parameter across every caller
 * in the codebase, so narrowing it only here would be cosmetic, not a real type guarantee.
 */
export const uploadAdditionalChargeFile = (
  url: string,
  extension: string,
  file: any
): Promise<APIResponseType<never>> => {
  return PUT(url, extension, file);
};

/**
 * Send for Approval. Ticked rows only. D1 — `POST /tasks/:taskId/submit` is contract-only in this
 * build: A3 does not implement it, B2 does. This call is wired fully to the contract and will fail with
 * `response.errorMessage` until B2 ships; no FE change is expected when it does.
 */
export const submitAdditionalChargeLines = ({
  taskId,
  lineIds,
}: {
  taskId: string;
  lineIds: string[];
}): Promise<APIResponseType<AdditionalChargeSubmitResponse>> => {
  return POST(`${API_BASE_URL}${ADDITIONAL_CHARGES_SUBMIT(taskId)}`, { lineIds });
};
